import type { AreaRole, AtaPresenca, GlobalRole } from './enums'

/**
 * Espelho, para a interface, das regras que o banco impõe (RLS + trigger
 * `supply.task_guard_fields`). A tela só esconde o que não pode; quem barra
 * de verdade é o banco.
 */

export interface Me {
  readonly memberId: string
  readonly roles: readonly GlobalRole[]
  readonly areas: readonly { readonly areaId: string; readonly role: AreaRole }[]
}

export function isGestor(me: Me | null | undefined): boolean {
  return !!me && (me.roles.includes('gestor') || me.roles.includes('admin'))
}

export function isAdmin(me: Me | null | undefined): boolean {
  return !!me && me.roles.includes('admin')
}

export function isAreaMember(me: Me | null | undefined, areaId: string | null | undefined): boolean {
  return !!me && !!areaId && me.areas.some((a) => a.areaId === areaId)
}

/**
 * Executor (`supply.is_executor`; colaborador é o mesmo papel): tem área, não
 * é responsável de nenhuma nem gestor. Lê e escreve só na própria área (uma
 * só); não vê telas gerenciais (ADR 0006).
 */
export function isExecutor(me: Me | null | undefined): boolean {
  return !!me && !isGestor(me) && me.areas.length > 0 && !me.areas.some((a) => a.role === 'responsavel')
}

/** Gerente = responsável de alguma área (`supply.is_gerente`). */
export function isGerente(me: Me | null | undefined): boolean {
  return !!me && me.areas.some((a) => a.role === 'responsavel')
}

/** Gerente da área (`supply.is_gerente_of`): traz e tira executores do time dela. */
export function isGerenteOf(me: Me | null | undefined, areaId: string | null | undefined): boolean {
  return !!me && !!areaId && me.areas.some((a) => a.areaId === areaId && a.role === 'responsavel')
}

/** Gestor, ou o gerente da área (`supply.add_team_executor`/`remove_team_executor`). */
export function canManageTeam(me: Me | null | undefined, areaId: string | null | undefined): boolean {
  return isGestor(me) || isGerenteOf(me, areaId)
}

/** Gestor escreve em qualquer área; gerente e executor só na(s) área(s) deles (`supply.can_write_area`). */
export function canWriteArea(me: Me | null | undefined, areaId: string | null | undefined): boolean {
  return isGestor(me) || isAreaMember(me, areaId)
}

/**
 * Telas que o executor não abre (a sidebar esconde, a rota redireciona para
 * Minha semana, e o banco nega os dados): indicadores gerenciais e administração.
 */
export const EXECUTOR_BLOCKED_ROUTES = ['/dashboard', '/desempenho', '/gestor', '/configuracoes', '/usuarios', '/carga'] as const

export function canAccessRoute(me: Me | null | undefined, pathname: string): boolean {
  if (!isExecutor(me)) return true
  return !EXECUTOR_BLOCKED_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`))
}

export interface TaskForPermission {
  readonly area_id: string
  readonly executor_id: string | null
}

/** Campos que o executor de fora da área pode mexer (o resto é da área). */
export const EXECUTOR_FIELDS = [
  'status',
  'qtd_entregue',
  'status_note',
  'q3_bom',
  'q3_pena',
  'position',
] as const
export type ExecutorField = (typeof EXECUTOR_FIELDS)[number]

export type TaskAccess = 'total' | 'executor' | 'leitura'

export function taskAccess(me: Me | null | undefined, task: TaskForPermission): TaskAccess {
  if (canWriteArea(me, task.area_id)) return 'total'
  if (me && task.executor_id === me.memberId) return 'executor'
  return 'leitura'
}

export function canEditField(access: TaskAccess, field: string): boolean {
  if (access === 'total') return true
  if (access === 'executor') return (EXECUTOR_FIELDS as readonly string[]).includes(field)
  return false
}

/** ATA de área: membros da área e gestor. ATA GERAL (sem área): só gestor. */
export function canWriteAta(me: Me | null | undefined, areaId: string | null): boolean {
  if (areaId === null) return isGestor(me)
  return canWriteArea(me, areaId)
}

/** Quantos convocados ainda estão com a presença a confirmar. */
export function presencaPendente(participants: readonly { readonly presenca: AtaPresenca }[]): number {
  return participants.filter((p) => p.presenca === 'pendente').length
}

/**
 * Fechar a ATA: quem escreve nela, e só com a presença de todos os convocados
 * confirmada (presente ou faltou) — `supply.close_ata` recusa o resto.
 */
export function canCloseAta(
  me: Me | null | undefined,
  areaId: string | null,
  participants: readonly { readonly presenca: AtaPresenca }[],
): boolean {
  return canWriteAta(me, areaId) && presencaPendente(participants) === 0
}

/** Local padrão de ATA: cadastra gestor ou quem tem área (`supply.can_manage_ata_local`). */
export function canAddAtaLocal(me: Me | null | undefined): boolean {
  return isGestor(me) || (!!me && me.areas.length > 0)
}

/** Vincular a área a um departamento do Portal de Chamados (`supply.area.pc_area`, policy `area_write`): só o gestor. */
export function canLinkPcArea(me: Me | null | undefined): boolean {
  return isGestor(me)
}

/**
 * Perfil de acesso, para mostrar na tela de Usuários e Permissões: o papel
 * mais forte que a pessoa tem. Não é regra nova — só resume as de cima.
 */
export type Perfil = 'admin' | 'gestor' | 'responsavel' | 'executor' | 'leitura'

export function perfilDe(me: Me | null | undefined): Perfil {
  if (isAdmin(me)) return 'admin'
  if (isGestor(me)) return 'gestor'
  if (me?.areas.some((a) => a.role === 'responsavel')) return 'responsavel'
  if (isExecutor(me)) return 'executor'
  return 'leitura'
}

/**
 * Acesso pendente: ficha ativa, mas sem papel global e sem área (veio do SSO
 * pelo "Importar do SSO" sem cargo). A sessão mostra "Acesso pendente — fale
 * com o administrador" em vez do app até o gestor ou o dev admin dar um cargo.
 */
export function isPendente(me: Me | null | undefined): boolean {
  return perfilDe(me) === 'leitura'
}

export const PERFIL_LABEL: Record<Perfil, string> = {
  admin: 'Dev admin',
  gestor: 'Gestor',
  responsavel: 'Gerente',
  executor: 'Executor',
  leitura: 'Acesso pendente',
}

/** O que cada perfil permite, nas palavras da tela (espelha as funções acima). */
export const PERFIL_PERMITE: Record<Perfil, readonly string[]> = {
  admin: [
    'Tudo o que o gestor faz: configura, importa pessoas do SSO, define papéis e usa "Ver como"',
    'Papel técnico de quem instala e mantém o sistema: só outro dev admin concede ou remove',
  ],
  gestor: [
    'Escreve em qualquer área e na ATA GERAL',
    'Administra usuários e as configurações (áreas, metas, prazos e avisos, modelos e locais de ATA)',
    'Restaura tarefas de qualquer área na lixeira',
  ],
  responsavel: [
    'Gerente: lê tudo (RAS conjunta) e escreve nas tarefas e ATAs das áreas dele',
    'Aparece como responsável nas tarefas e no desempenho (um por área)',
    'Traz e tira executores do próprio time',
    'Cria rotinas da área e vê a Carga do time dele',
    'Cadastra locais de ATA',
  ],
  executor: [
    'Do time de um gerente, numa área só (colaborador é o mesmo papel)',
    'Lê e escreve só na própria área: tarefas, ações, ATAs e decisões dela',
    'ATA GERAL só se for convocado, e só leitura',
    'Vê e marca as próprias rotinas e as do time da área',
    'Sem Dashboard, Desempenho, Carga do time, Configurações e Usuários',
  ],
  leitura: [
    'Entrou pelo SSO, mas ainda sem papel nem cargo no Supply',
    'Vê só a tela "Acesso pendente" até o gestor ou o dev admin dar um cargo',
  ],
}

/** Dia ideal das reuniões RAS e Q3: só gestor (RLS de `supply.meeting_schedule`). */
export function canEditMeetingSchedule(me: Me | null | undefined): boolean {
  return isGestor(me)
}

/** Configurações do gestor: só gestor grava (RLS de `supply.setting`). */
export function canEditSettings(me: Me | null | undefined): boolean {
  return isGestor(me)
}

/** Rotina da área: cria, edita e exclui o gestor ou o gerente da área (`supply.can_manage_routine`). */
export function canManageRoutine(me: Me | null | undefined, areaId: string | null | undefined): boolean {
  return isGestor(me) || isGerenteOf(me, areaId)
}

/** Cria rotina em alguma área (botão "Nova rotina"). */
export function canCreateRoutine(me: Me | null | undefined): boolean {
  return isGestor(me) || isGerente(me)
}

export interface RoutineForPermission {
  readonly area_id: string
  /** Nulo = rotina do time da área. */
  readonly assignee_id: string | null
}

/**
 * Marca a ocorrência (`supply.can_check_routine`): o responsável; rotina do
 * time, quem é da área; e quem gerencia a rotina.
 */
export function canCheckRoutine(me: Me | null | undefined, routine: RoutineForPermission): boolean {
  if (!me) return false
  if (canManageRoutine(me, routine.area_id)) return true
  if (routine.assignee_id !== null) return routine.assignee_id === me.memberId
  return isAreaMember(me, routine.area_id)
}

/** Renomeia ou remove o local: gestor, ou quem cadastrou (RLS update/delete de `supply.ata_local`). */
export function canRemoveAtaLocal(me: Me | null | undefined, createdBy: string | null): boolean {
  return isGestor(me) || (!!me && createdBy === me.memberId)
}

/**
 * Dev admin fantasma (`supply.is_ghost`, ADR 0008): papel `admin` sem `gestor`
 * e sem área. Vê e configura tudo, mas não existe para o resto do sistema —
 * o banco não entrega a ficha a quem não é admin. Admin com papel operacional
 * (gestor ou área) aparece normalmente pelo papel operacional.
 */
export function isGhostAdmin(me: Me | null | undefined): boolean {
  return !!me && isAdmin(me) && !me.roles.includes('gestor') && me.areas.length === 0
}

/** Conceder ou revogar dev admin: só outro admin (`supply.guard_admin_role`), nunca o último. */
export function canGrantAdmin(me: Me | null | undefined): boolean {
  return isAdmin(me)
}

/** Como o autor fantasma aparece para quem não é admin (`supply.member_display_name`). */
export const SYSTEM_ACTOR_NAME = 'Administração do sistema'

/** Rótulo do papel operacional, igual em toda tela (Pessoas, organograma, filtros). */
export type Papel = 'gestor' | 'gerente' | 'executor'

export const PAPEL_LABEL: Record<Papel, string> = {
  gestor: 'Gestor',
  gerente: 'Gerente',
  executor: 'Executor',
}

/** Papel operacional de quem tem um; dev admin puro e acesso pendente não têm. */
export function papelDe(me: Me | null | undefined): Papel | null {
  if (!me) return null
  if (me.roles.includes('gestor')) return 'gestor'
  if (isGerente(me)) return 'gerente'
  if (me.areas.length > 0) return 'executor'
  return null
}
